import { useEffect, useState } from "react";
import {
  Modal,
  Box,
  IconButton,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { assetImages } from "../../../constants/assets";
import TabButtons from "../wallet/tab-buttons";
import AmountInput from "../wallet/amount-input";
import AmountButtons from "../wallet/amount-button";
import PaymentMethod from "../wallet/payment-method";
import GradientButton from "../gradient-button";
import englishTranslations from "../../../i18n/translations/en";
import WithdrawContent from "../wallet/withdraw-content";
import { fontSizes } from "../../../theme/font-sizes";
import useApiRequest from "../../../api/api";
import apiRoutes from "../../../api/routes";
import { useUser } from "../../../context/user-context";
// import { provider } from "../wallet/setup";
import { BrowserProvider, Contract, formatUnits, parseUnits } from "ethers";
import LoadingBackdrop from "../loader";
import { metamaskProvider, MMSDK } from "../wallet/metamask";
import { coinbaseProvider, coinbaseSDK } from "../wallet/coinbase";
import { ethers } from "ethers";
import { useSnackbar } from "../SnackbarProvider";
const StyledModal = styled(Modal)(({ isMediumScreen }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ModalContent = styled(Box)(({ isMediumScreen, isSmallScreen }) => ({
  width: isMediumScreen ? (isSmallScreen ? "85%" : 410) : 440,
  minheight: isMediumScreen ? "80vh" : "auto",
  maxHeight: isMediumScreen ? "82vh" : "auto",
  borderRadius: "22px",
  backgroundColor: "#141414",
  position: "relative",
  padding: 0,
  overflowY: "auto",
  overflowX: "hidden",
  "&::-webkit-scrollbar": {
    display: "none", // Hide scrollbar in WebKit browsers (Chrome, Safari)
  },
}));

const ContentSection = styled(Box)(({ isMediumScreen }) => ({
  padding: isMediumScreen ? 12 : "30px",
  paddingBottom: 25,
  marginTop: 25,
  display: "flex",
  flexDirection: "column",
  gap: "20px",
}));
const WithDrawSection = styled(Box)({
  // padding: "30px",
  marginTop: 30,
  display: "flex",
  flexDirection: "column",
  gap: "20px",
});

export default function WalletPopup({ open = true, onClose, currentTab }) {
  const [amount, setAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [activeTab, setActiveTab] = useState(currentTab || "topup");
  const [selectedAmount, setSelectedAmount] = useState(null);
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { makeApiCall } = useApiRequest();
  const { user, updateUser } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const showSnackbar = useSnackbar();
  const isMetaMask = user?.wallet?.name === "metamask";
  const isCoinbase = user?.wallet?.name === "coinbase";
  const isTrustWallet = user?.wallet?.name === "trustwallet";
  const amounts = [50, 150, 250, 500, 1000];

  const handleAmountClick = (value) => {
    setSelectedAmount(value);
    setAmount(value.toString());
  };
  const handlePopupClose = () => {
    setAmount("");
    setSelectedAmount(null);
    onClose();
  };
  const fetchProvider = () => {
    if (isMetaMask) return metamaskProvider;
    if (isCoinbase) return coinbaseProvider;
    if (isTrustWallet) return window.ethereum;
    return null;
  };

  const handleTopup = async () => {
    const USDC_CONTRACT_ADDRESS = "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48";
    const DAPP_WALLET_ADDRESS = process.env.REACT_APP_DAPP_WALLET_ADDRESS;
    window.addEventListener("beforeunload", preventExit);

    try {
      const provider = fetchProvider();

      if (isMetaMask) await provider.terminate(); // Clears any corrupted session
      let web3Instance;
      if (isTrustWallet) {
        web3Instance = new BrowserProvider(provider);
      } else {
        web3Instance = new BrowserProvider(provider.getProvider());
      }
      // let signer;
      // try {
      //   signer = await web3Instance.getSigner(user.wallet.address);
      // } catch (error) {
      //   console.log("User not connected or authorized. Requesting accounts...");
      //   // If the previous call failed due to not requesting accounts, request it now
      //   await provider.request({ method: "eth_requestAccounts" });
      //   signer = await web3Instance.getSigner(user.wallet.address); // Retry with the signer
      // }
      if (isTrustWallet) {
        await window.ethereum.request({
          method: "eth_requestAccounts",
        });
      } else {
        await provider.getProvider().request({ method: "eth_requestAccounts" });
      }

      const signer = await web3Instance.getSigner(); // Works for MetaMask
      const network = await web3Instance.getNetwork();

      // Validate that user is on Ethereum Mainnet (chainId: 1)
      const ETHEREUM_MAINNET_CHAIN_ID = 1n; // Ethereum mainnet chain ID
      if (network.chainId !== ETHEREUM_MAINNET_CHAIN_ID) {
        showSnackbar(
          `Please switch to Ethereum Mainnet. Current network chain ID: ${network.chainId}. Required: ${ETHEREUM_MAINNET_CHAIN_ID}`,
          "error"
        );
        return;
      }

      // Convert amount to USDC (6 decimal places)
      const ERC20_ABI = [
        "function transfer(address to, uint256 value) public returns (bool)",
        "function balanceOf(address owner) view returns (uint256)",
        "function name() view returns (string)",
      ];

      const usdcContract = new Contract(
        USDC_CONTRACT_ADDRESS,
        ERC20_ABI,
        signer
      );
      const userAddress = await signer.getAddress();

      // Check USDC balance before transaction
      const balanceBefore = await usdcContract.balanceOf(userAddress);
      const balanceBeforeFormatted = formatUnits(balanceBefore, 6);
      console.log(`USDC Balance before: ${balanceBeforeFormatted} USDC`);

      const usdcAmount = parseUnits(amount.toString(), 6);
      const amountFormatted = formatUnits(usdcAmount, 6);
      console.log(`Attempting to transfer: ${amountFormatted} USDC`);

      // Verify user has sufficient balance
      if (balanceBefore < usdcAmount) {
        showSnackbar(
          `Insufficient balance. You have ${balanceBeforeFormatted} USDC, but need ${amountFormatted} USDC`,
          "error"
        );
        return;
      }

      // Estimate gas to catch errors early
      try {
        await usdcContract.transfer.estimateGas(
          DAPP_WALLET_ADDRESS,
          usdcAmount
        );
      } catch (estimateError) {
        console.error("Gas estimation failed:", estimateError);
        if (
          estimateError.reason?.includes("exceeds balance") ||
          estimateError.message?.includes("exceeds balance")
        ) {
          showSnackbar(
            `Insufficient USDC balance. You have ${balanceBeforeFormatted} USDC, but need ${amountFormatted} USDC`,
            "error"
          );
        } else {
          showSnackbar(
            "Transaction will likely fail. Please check your balance and try again.",
            "error"
          );
        }
        return;
      }

      // Start loading before transaction
      setIsLoading(true);

      const tx = await usdcContract.transfer(DAPP_WALLET_ADDRESS, usdcAmount);
      console.log(`Transaction hash: ${tx.hash}`);
      console.log("Waiting for transaction confirmation...");

      const receipt = await tx.wait(); // Wait for confirmation
      console.log("Transaction confirmed!");

      // Check if transaction was successful (status === 1 means success, status === 0 means failure)
      if (receipt && receipt.status === 1 && tx.hash) {
        const balanceAfter = await usdcContract.balanceOf(userAddress);
        const balanceAfterFormatted = formatUnits(balanceAfter, 6);
        const balanceDifference = balanceBefore - balanceAfter;
        const balanceDifferenceFormatted = formatUnits(balanceDifference, 6);

        console.log(`USDC Balance after: ${balanceAfterFormatted} USDC`);
        console.log(`USDC Deducted: ${balanceDifferenceFormatted} USDC`);

        // Verify the balance decreased by the expected amount (with small tolerance for rounding)
        const expectedDeduction = parseUnits(amount.toString(), 6);
        const tolerance = parseUnits("0.000001", 6); // 0.000001 USDC tolerance

        if (balanceDifference < expectedDeduction - tolerance) {
          console.error("USDC deduction verification failed!");
          console.error(
            `Expected to deduct: ${amount} USDC, but only deducted: ${balanceDifferenceFormatted} USDC`
          );
          throw new Error(
            "USDC transfer verification failed. Please check your balance."
          );
        }

        // Verify Transfer event was emitted in the receipt logs
        const transferEventSignature =
          "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef"; // Transfer(address,address,uint256)
        const transferLogs = receipt.logs.filter(
          (log) =>
            log.address.toLowerCase() === USDC_CONTRACT_ADDRESS.toLowerCase() &&
            log.topics[0] === transferEventSignature &&
            log.topics[1]?.toLowerCase() ===
              userAddress
                .toLowerCase()
                .replace(/^0x/, "0x000000000000000000000000")
        );

        if (transferLogs.length === 0) {
          console.error("Transfer event not found in transaction logs!");
          throw new Error(
            "USDC transfer event not found. Transaction may have failed."
          );
        }

        console.log("✅ USDC transfer verified successfully!");
        console.log(
          `✅ Balance decreased by ${balanceDifferenceFormatted} USDC as expected`
        );
        const payload = {
          amount: amount,
          currency: "usdt",
          transactionHash: tx.hash,
        };
        const response = await makeApiCall(
          apiRoutes.wallet.topup,
          "POST",
          payload,
          "show"
        );
        if (response.success) {
          const updatedUser = {
            ...user,
            walletAmount: response.data.walletAmount,
          };
          localStorage.setItem("user", JSON.stringify(updatedUser));
          updateUser(updatedUser);
        }
        handlePopupClose();
      } else {
        // Transaction failed or was reverted
        showSnackbar("Transaction failed or was reverted", "error");
        return;
      }
    } catch (error) {
      console.error("Topup error:", error);

      // Handle user rejection gracefully (don't show as error)
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("user rejected")
      ) {
        console.log("User rejected the transaction");
        // Don't show error message for user cancellation
        return;
      }

      // Handle insufficient balance error
      if (
        error.reason?.includes("exceeds balance") ||
        error.message?.includes("exceeds balance") ||
        error.reason?.includes("ERC20: transfer amount exceeds balance")
      ) {
        showSnackbar(
          "Insufficient USDC balance. Please check your wallet balance.",
          "error"
        );
        return;
      }

      // Handle other errors
      let errorMessage = "Transaction failed. Please try again.";

      if (error.reason) {
        errorMessage = error.reason;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      // Stop loading and remove the event listener after transaction completes
      setIsLoading(false);
      window.removeEventListener("beforeunload", preventExit);
    }
  };

  const preventExit = (event) => {
    event.preventDefault();
  };

  // Hook to remove event listener when component unmounts
  useEffect(() => {
    return () => {
      window.removeEventListener("beforeunload", preventExit);
    };
  }, []);
  useEffect(() => {
    if (currentTab) {
      setActiveTab(currentTab);
    }
  }, [currentTab]);
  const handleWithdraw = async () => {
    const payload = {
      amount: withdrawAmount,
      currency: "usdt",
    };
    try {
      const response = await makeApiCall(
        apiRoutes.wallet.withdraw,
        "POST",
        payload,
        "show"
      );
      handlePopupClose();
    } catch (e) {
      console.log(e);
    }
  };
  return (
    <StyledModal
      open={open}
      onClose={handlePopupClose}
      isMediumScreen={isMediumScreen}
    >
      <ModalContent
        isMediumScreen={isMediumScreen}
        isSmallScreen={isSmallScreen}
      >
        <LoadingBackdrop
          isLoading={isLoading}
          message="Transaction is being processed, please wait..."
        />

        <Box
          sx={{
            position: "relative",
            height: isMediumScreen ? 100 : 90,
          }}
        >
          <Box
            component="img"
            src={assetImages.walletBg}
            alt="Background"
            layout="fill"
            objectFit="cover"
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: isMediumScreen ? 55 : 30,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Box
            component="img"
            width={isMediumScreen ? 75 : "auto"}
            src={assetImages.walletLogo}
            alt="Logo"
          />
        </Box>
        <IconButton
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
          onClick={handlePopupClose}
        >
          <CloseIcon />
        </IconButton>
        {activeTab === "topup" ? (
          <ContentSection isMediumScreen={isMediumScreen}>
            <TabButtons
              isMediumScreen={isMediumScreen}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />

            <>
              {" "}
              <AmountInput amount={amount} setAmount={setAmount} />
              <AmountButtons
                amounts={amounts}
                selectedAmount={selectedAmount}
                onAmountClick={handleAmountClick}
              />
              <PaymentMethod isMediumScreen={isMediumScreen} />
              <GradientButton
                text={englishTranslations.wallet.topup}
                onClick={handleTopup}
                fontSize={fontSizes.heading2}
                disabled={!amount || Number(amount) === 0}
              />
            </>
          </ContentSection>
        ) : (
          <WithDrawSection>
            <Box padding={isMediumScreen ? 2 : "30px"}>
              <TabButtons
                isMediumScreen={isMediumScreen}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
            </Box>

            <WithdrawContent
              isMediumScreen={isMediumScreen}
              withdrawAmount={withdrawAmount}
              setWithdrawAmount={setWithdrawAmount}
              handleWithdraw={handleWithdraw}
            />
          </WithDrawSection>
        )}
      </ModalContent>
    </StyledModal>
  );
}
